import Swiper from "swiper";
import { A11y, Autoplay, EffectFade, Pagination, Scrollbar } from "swiper/modules";
import "@styles/components/slider.css";
import easydropdown from "easydropdown-a11y";
import "@styles/components/dropdown.css";
import { on } from "delegated-events";
import ajax from "@common/ajax";
import { scrollToIfNeeded } from "@common/scroll-to";
import { generateQueryString } from "@common/util";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";

const state = {
  calendar: null,
};

const form = document.querySelector(".filter-form");

const setupDropdown = (el) => {
  if (el.tagName !== "SELECT") {
    return;
  }

  const opts = {
    behavior: {
      openOnFocus: true,
      maxVisibleItems: 10,
      liveUpdates: true,
      useNativeUiOnMobile: false,
    },
  };

  easydropdown(el, opts);
};

const handleFilterChange = (e) => {
  e && e.preventDefault();

  const params = new FormData(form);
  const queryString = generateQueryString(params);

  const windowUrl = new URL(window.location.href);
  const windowParams = new URLSearchParams(windowUrl.search);

  if (windowParams.has("view")) {
    queryString.set("view", windowParams.get("view"));
  }

  const url = queryString.size ? `${form.action}?${queryString}` : form.action;

  window.history.replaceState({}, "", url);

  updateResults();
};

const handleFilterClear = () => {
  form.reset();
  form.querySelectorAll("select").forEach((x) => (x.value = ""));

  window.history.replaceState({}, "", form.action);

  updateResults();

  document.body.classList.remove("is-searching");
};

const handleView = (e) => {
  const button = e.target.closest("button");

  document.querySelectorAll(".events-view-controls .is-active").forEach((x) => x.classList.remove("is-active"));

  button.classList.add("is-active");

  const gridView = document.querySelector(".events-view-grid");
  const calendarView = document.querySelector(".events-view-calendar");
  const pastEvents = document.querySelector(".events-past");

  let selectedView = "";

  if (button.dataset.view === "grid") {
    gridView.classList.remove("hidden");
    calendarView.classList.add("hidden");
    selectedView = "grid";

    if (pastEvents) {
      pastEvents.classList.remove("hidden");
    }

    setupEventSliders();
  } else {
    gridView.classList.add("hidden");
    calendarView.classList.remove("hidden");
    selectedView = "calendar";

    if (pastEvents) {
      pastEvents.classList.add("hidden");
    }

    setupCalendar();
  }

  const windowUrl = new URL(window.location.href);
  const params = new URLSearchParams(windowUrl.search);

  params.set("view", selectedView);

  windowUrl.search = params.toString();

  window.history.replaceState({}, "", windowUrl);
};

const handleSwitchToGridView = () => {
  const selected = document.querySelector(".events-view-controls .is-active");

  if (selected.dataset.view === "calendar") {
    selected.classList.remove("is-active");

    document.querySelector('.events-view-controls [data-view="grid"]').classList.add("is-active");

    const gridView = document.querySelector(".events-view-grid");
    const calendarView = document.querySelector(".events-view-calendar");

    gridView.classList.remove("hidden");
    calendarView.classList.add("hidden");

    const windowUrl = new URL(window.location.href);
    const params = new URLSearchParams(windowUrl.search);

    params.set("view", "grid");

    windowUrl.search = params.toString();

    window.history.replaceState({}, "", windowUrl);
  }
};

const updateResults = async () => {
  const pageContent = document.querySelector("[data-page-content]");

  pageContent.classList.add("is-loading");

  const res = await ajax.get(window.location.href).json();

  pageContent.innerHTML = res.page;
  pageContent.classList.remove("is-loading");

  if (res.isFiltered) {
    form.classList.add("is-filtered");
    scrollToIfNeeded(pageContent, 24);
  } else {
    form.classList.remove("is-filtered");
  }

  setupEventSliders();
  setupCalendar();
};

const setupFeaturedSlider = () => {
  const slider = document.querySelector(".featured-slider");

  if (slider) {
    const container = slider.querySelector(".swiper");
    const numSlides = slider.querySelectorAll(".swiper-slide").length;

    if (numSlides > 1) {
      const opts = {
        modules: [A11y, Autoplay, EffectFade, Pagination],
        autoplay: {
          delay: 6000,
          pauseOnMouseEnter: true,
        },
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        loop: true,
        speed: 1000,
        allowTouchMove: false,
        simulateTouch: false,
      };

      const swiper = new Swiper(container, opts);

      on("click", ".featured-slider .next-slide", () => swiper.slideNext());
      on("click", ".featured-slider .prev-slide", () => swiper.slidePrev());
    }
  }
};

const setupEventSliders = () => {
  document.querySelectorAll(".events-slider").forEach((el) => {
    if (el.classList.contains("did-init")) {
      return;
    }

    el.classList.add("did-init");

    const container = el.querySelector(".swiper");

    const swiper = new Swiper(container, {
      modules: [A11y, Autoplay, Scrollbar],
      autoplay: {
        delay: 4000,
        pauseOnMouseEnter: true,
      },
      slidesPerView: 1,
      spaceBetween: 16,
      speed: 500,
      freeMode: true,
      scrollbar: {
        el: el.querySelector(".swiper-scrollbar"),
        draggable: true,
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 24,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 36,
        },
      },
    });

    const showAllToggles = el.querySelectorAll(".show-all-toggle");

    if (showAllToggles.length) {
      showAllToggles.forEach((button) => {
        button.addEventListener("click", () => {
          el.classList.toggle("is-show-all");

          if (el.classList.contains("is-show-all")) {
            swiper.slideTo(0, 0);
            swiper.disable();

            handleSwitchToGridView();
          } else {
            el.classList.add("is-enabling");
            swiper.enable();

            setTimeout(() => {
              el.classList.remove("is-enabling");
            }, 300);
          }
        });
      });
    }
  });
};

const setupCalendar = () => {
  const el = document.getElementById("events-calendar");

  if (!el) {
    return;
  }

  if (!el.classList.contains("fc")) {
    const endpoint = el.dataset.endpoint;

    state.calendar = new Calendar(el, {
      plugins: [dayGridPlugin],
      initialView: "dayGridMonth",
      aspectRatio: 1.6,
      dayMaxEvents: window.matchMedia("(min-width: 1024px)").matches ? 3 : 0,
      headerToolbar: {
        left: "",
        center: "prev,title,next",
        right: "",
      },
      eventTimeFormat: {
        hour: "numeric",
        meridiem: "short",
      },
      events: async (info, success) => {
        const query = { start: info.startStr, end: info.endStr };
        const urlParams = new URLSearchParams(window.location.search);
        const searchCategory = urlParams.get("category");
        const searchTerm = urlParams.get("search");

        if (searchCategory) {
          query.category = searchCategory;
        }

        if (searchTerm) {
          query.search = searchTerm;
        }

        const resp = await ajax.url(endpoint).query(query).get().json();

        success(resp);

        el.querySelectorAll("a.fc-event").forEach((anchor) => {
          if (anchor.href.includes("_modal")) {
            anchor.setAttribute("data-modal", "content");
            anchor.setAttribute("data-modal-use-custom-close", "true");
          }
        });
      },
      moreLinkClick: () => {
        setTimeout(() => {
          document.querySelectorAll(".fc-popover-body a.fc-event").forEach((anchor) => {
            if (anchor.href.includes("_modal")) {
              anchor.setAttribute("data-modal", "content");
              anchor.setAttribute("data-modal-use-custom-close", "true");
            }
          });
        }, 16);

        return "popover";
      },
    });
  }

  state.calendar.render();
};

const init = () => {
  if (form) {
    form.querySelectorAll("select:not([multiple])").forEach((el) => setupDropdown(el));

    on("change", ".filter-form select", handleFilterChange);
    on("submit", ".filter-form", handleFilterChange);
    on("click", ".filter-form-clear", handleFilterClear);

    on("click", ".search-toggle", () => {
      document.body.classList.toggle("is-searching");

      if (document.body.classList.contains("is-searching")) {
        form.querySelector('input[type="search"]').focus();
      }
    });

    on("click", ".events-view-controls button", handleView);
  }

  setupFeaturedSlider();
  setupEventSliders();
  setupCalendar();
};

init();
